/**
 * Decoding an HTTP response into an API payload — the pure half of `client.ts`,
 * extracted so it can actually be executed in tests. `client.ts` imports
 * `expo-constants`, `react-native` and `i18n`, so nothing in it can run outside
 * a device runtime; this module imports nothing.
 *
 * **Why this exists as its own module: a 2xx is not a promise of JSON.**
 * Measured 2026-08-07 against `dev-api.opncue.com` with Cloudflare Access set
 * to *Allow*: `GET /users/me` answered **302** to
 * `opncue.cloudflareaccess.com/cdn-cgi/access/login/…`, and because `fetch()`
 * follows redirects by default the app saw **200 `text/html`** — a 32KB
 * "Sign in ・ Cloudflare Access" page. The old code did `JSON.parse` in a
 * try/catch and returned the RAW TEXT on failure, so that HTML was handed back
 * as a typed `User`, persisted to storage, and read by `needsProfileSetup()` as
 * "profile incomplete" — which routed a fully signed-in user to onboarding, and
 * kept doing it after a restart because the corrupted value was cached.
 *
 * Cloudflare is only the instance. Any captive portal, corporate proxy, ISP
 * interstitial or misrouted CDN does the same thing: a cheerful 200 wrapping
 * someone else's HTML. The rule that holds against all of them is that **an OK
 * response whose body is not JSON is a failed request**, not a successful one
 * carrying a surprise.
 */

// Fields are declared and assigned explicitly rather than via TypeScript
// parameter properties: this module is loaded directly by `node --test`, whose
// type stripping is syntax-only and rejects `constructor(public x: T)`.
export class ApiError extends Error {
  status: number;
  body?: unknown;
  /**
   * How long the server asked us to wait, in milliseconds, when it refused for
   * BACKPRESSURE (429/503) rather than for a problem with the request.
   *
   * Carried on the error rather than swallowed inside the client, because the
   * decision of whether to retry belongs to the caller: a background beat
   * should quietly wait and try again, while a user-initiated action should
   * usually surface something instead of silently stalling. Both need the
   * number, and neither can recover it from a thrown error that dropped it.
   *
   * `undefined` on every other kind of failure — a 400 is not going to become
   * a 200 by waiting.
   */
  retryAfterMs?: number;

  constructor(
    status: number,
    message: string,
    body?: unknown,
    retryAfterMs?: number,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.retryAfterMs = retryAfterMs;
  }
}

/** A 2xx whose body is not JSON — almost always something answering *for* the API. */
export class NonJsonResponseError extends ApiError {
  /** Where the response actually came from, when the runtime exposes it. */
  finalUrl?: string;

  constructor(status: number, message: string, finalUrl?: string) {
    super(status, message);
    this.name = "NonJsonResponseError";
    this.finalUrl = finalUrl;
  }
}

export type DecodeInput = {
  ok: boolean;
  status: number;
  statusText: string;
  /** `res.url` — after redirects. Diagnostic only; never used to decide. */
  finalUrl?: string;
  /** The already-read body. */
  text: string;
  /** Localised fallback, passed in so this module stays free of i18n. */
  genericMessage: string;
};

function parseOrUndefined(text: string): { data: unknown; isJson: boolean } {
  try {
    return { data: JSON.parse(text), isJson: true };
  } catch {
    return { data: undefined, isJson: false };
  }
}

/**
 * Turn a response into its payload, or throw.
 *
 * An empty body is fine and yields `undefined` — `request<void>` endpoints
 * (logout, app-open, mood-jar delete) legitimately answer 204/empty. What is
 * never fine is a NON-empty 2xx body that is not JSON.
 */
export function decodeApiResponse<T>(input: DecodeInput): T {
  const { ok, status, statusText, finalUrl, text, genericMessage } = input;

  if (!text) {
    if (!ok) throw new ApiError(status, statusText || genericMessage);
    return undefined as T;
  }

  const { data, isJson } = parseOrUndefined(text);

  if (!ok) {
    // Unchanged behaviour on the error path: prefer the API's own message.
    const message =
      (data && typeof data === "object" && "message" in data
        ? String((data as Record<string, unknown>).message)
        : statusText) || genericMessage;
    throw new ApiError(status, message, data);
  }

  if (!isJson) {
    // The body is NOT included in the error: it is someone else's HTML, it can
    // be tens of kilobytes, and it may carry a login form or tokens. The final
    // URL is the useful diagnostic — it names who actually answered.
    throw new NonJsonResponseError(
      status,
      `Expected JSON from the API but received a non-JSON ${status} response` +
        (finalUrl ? ` from ${finalUrl}` : "") +
        ". The request was probably intercepted (access gateway, captive portal or proxy).",
      finalUrl,
    );
  }

  return data as T;
}
